import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Observable } from 'rxjs/internal/Observable';

import { Message, Task } from '@app/models';
import { CredentialingChecklistItem, CredentialingSetupsService } from '../credentialing-setups/credentialing-setups.service';
import { CredentialingRequiredItemsService, DocumentService, DropdownService, GenericModuleService, InteractiveDocumentInstanceService, MessageService, SharedUtilsService, SsModuleService, SubService, TaskService, TeamService, UserService } from '@app/services';
import { InteractiveDocumentsService } from '@app/modules/interactive-documents/interactive-documents.service';
import { InteractiveDocumentInstance } from '@app/models/interactiveDocumentInstance';

export interface CredentialingApi {
  items: any[];
  total_count: number;
}

@Injectable({ providedIn: 'root' })
export class CredentialingService {
  private currentCredentialing;

  public currentSearch = {
    params: null,
    results: null
  };

  private API_URL = '/api/credentialing/';

  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  public subStatuses = null;

  constructor(
    private httpClient: HttpClient,
    private _credentialingRequiredItemsService: CredentialingRequiredItemsService,
    private _credentialingSetupService: CredentialingSetupsService,
    private _documentService: DocumentService,
    private _dropdownService: DropdownService,
    private _genericModuleService: GenericModuleService,
    private _messageService: MessageService,
    private _moduleService: SsModuleService,
    private _subService: SubService,
    private _interactiveDocumentService: InteractiveDocumentsService,
    private _interactiveDocumentInstanceService: InteractiveDocumentInstanceService,
    private _sharedUtilsService: SharedUtilsService,
    private _taskService: TaskService,
    private _teamService: TeamService,
    private _userService: UserService
  ) { }

  setCurrent(_credentialing): void {
    this.currentCredentialing = _credentialing;
  }

  getCurrent() {
    return this.currentCredentialing;
  }


  create(_credentialingRecord): Observable<any> {
    return this.httpClient.post<any>(this.API_URL, JSON.stringify(_credentialingRecord), this.headerOptions);
  }

  // updateFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<any[]> {
  //   const updateRequest = {ids: arrayOfIds, fields: fieldsToUpdate};
  //   return this.httpClient.put<any[]>(this.API_URL + 'update-certain-fields/', JSON.stringify(updateRequest), this.headerOptions);
  // }


  updateFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<any[]> {
    const updateRequest = { ids: arrayOfIds, fields: fieldsToUpdate };
    return this.httpClient.put<any[]>(this.API_URL + 'update-certain-fields-array/', JSON.stringify(updateRequest), this.headerOptions);
  }


  updateFieldsById(_id, fieldsToUpdate): Observable<CredentialingApi> {
    return this.httpClient.put<CredentialingApi>(this.API_URL + 'update-certain-fields/' + _id, JSON.stringify(fieldsToUpdate), this.headerOptions);
  }


  update(_credentialing): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + _credentialing._id, JSON.stringify(_credentialing), this.headerOptions);
  }


  updateFavorites(_credentialing): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + 'favorite/' + _credentialing._id, JSON.stringify(_credentialing), this.headerOptions);
  }


  updateLastActivityDate(_credentialingId: string): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + 'update-last-action-date/' + _credentialingId, JSON.stringify({_id: _credentialingId}), this.headerOptions);
  }


  search(searchTerms): Observable<any[]> {
    searchTerms['selectAll'] = true;
    searchTerms['selectNumberOfRecords'] = false;
    return this.httpClient.post<any[]>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  normalSearch(searchTerms): Observable<any[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<any[]>(this.API_URL + 'normal-search', JSON.stringify(searchTerms), this.headerOptions);
  }


  getCredentialingRecord(_id: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + _id);
  }


  getWithRelationships(_moduleOptions): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'with-relationships/' + _moduleOptions.searchTerms._id, JSON.stringify(_moduleOptions), this.headerOptions);
  }


  delete(id: string): Observable<any> {
    return this.httpClient.put(this.API_URL + 'mark-deleted/' + id, this.headerOptions);
  }


  selectAllSearch(searchTerms): Observable<CredentialingApi> {
    searchTerms['selectAll'] = true;
    searchTerms['selectNumberOfRecords'] = false;
    return this.httpClient.post<CredentialingApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  selectNumberOfRecordsSearch(searchParams): Observable<any> {
    searchParams['selectAll'] = false;
    searchParams['selectNumberOfRecords'] = true;
    return this.httpClient.post<any>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  getCustomDatasource(searchParams, sortField = 'name', sortDirection = 'asc', pageNumber: number = 0, pageSize: number = 10): Observable<CredentialingApi> {
    searchParams['selectAll'] = false;
    searchParams['selectNumberOfRecords'] = false;

    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;

    return this.httpClient.post<CredentialingApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  checkAndCreateCredentialing(_placementRecord, relatedUser, _currentUser) {
    return new Promise(async (resolve) => {
      // console.log('_placementRecord: ', _placementRecord);
      try {
        this._subService.setHireStep('credentialing', 'active', 'Loading credentialing setup…'); // Update process step.

        let savedCredentialingRecord = null;

        const placementType: string = _placementRecord?.placement_type ?? null;
        const relatedAccountId: string = await this.getRelatedAccountId(_placementRecord) ?? null;

        // placement.profession is Object-typed but normally holds the dropdown string value.
        const _prof = _placementRecord?.profession;
        const professionValue = (typeof _prof === 'string') ? _prof : (_prof?.value ?? _prof?.name ?? null);

        // Lookup cascade: account + profession -> account (profession unset) -> default Core (W2 Core / 1099 Core).
        // Account-specific setups win for ANY account (managed or core) and may themselves be Core or Managed,
        // so tiers 1-2 key off account_specific only and don't constrain core_or_managed.
        let credentialingSetup = null;

        if (placementType?.length) {
          if (relatedAccountId) {
            // Tier 1: account + profession (most specific).
            if (professionValue) {
              credentialingSetup = await lastValueFrom(this._credentialingSetupService.searchForOne({
                provider_type: [placementType], account_specific: relatedAccountId, profession: professionValue
              }));
            }

            // Tier 2: account, profession unset (account-wide setup).
            if (!credentialingSetup?._id) {
              credentialingSetup = await lastValueFrom(this._credentialingSetupService.searchForOne({
                provider_type: [placementType], account_specific: relatedAccountId, profession_unset: true
              }));
            }
          }

          // Tier 3: global default Core setup (W2 Core / 1099 Core) — explicitly account-less.
          if (!credentialingSetup?._id) {
            credentialingSetup = await lastValueFrom(this._credentialingSetupService.searchForOne({
              provider_type: [placementType], core_or_managed: 'Core', account_specific_unset: true
            }));
          }
        }
        // console.log('credentialingSetup: ', credentialingSetup);

        if (credentialingSetup?._id) {
          this._subService.setHireStep('credentialing', 'done', 'Credentialing setup found.');
          const required_items = (credentialingSetup && credentialingSetup.required_items) ? credentialingSetup.required_items : null;

          if (required_items) {
            // console.log('Creating required items for this guy: ', required_items);

            // See if there are any previous credentialing records for this placement
            const placementSub = _placementRecord['subs_placements'];
            const subId = (placementSub && placementSub._id) ? placementSub._id : placementSub;

            const providerModule = await this._moduleService.getFullModuleByName('providers');
            const providerFields = await this._moduleService.getModuleFieldsWithRelationships(providerModule);

            const genericSearchData = {
              name: 'providers',
              schema: providerModule.customSchema,
              searchTerms: {
                fields: providerFields,
                inclusionSearch: {
                  subs_providers: { inclusionType: 'Include', value: [subId] }
                }
              },
              fields: providerFields,
              relationshipsNeeded: null
            };

            const relatedProviders = await lastValueFrom(this._genericModuleService.smartSearchUpdated(genericSearchData));
            // console.log('relatedProviders: ', relatedProviders);

            let allPreviousRequiredItems: CredentialingChecklistItem[] = null;
            let provider = null;

            if (relatedProviders && relatedProviders.length) {
              provider = relatedProviders[0];
              const providerUser = provider.related_user;

              // console.log('providerUser: ', providerUser);

              if (providerUser) {
                // console.log('Checking if there are any previous credentialing records for this placement');
                const previousCredentialingRecords = await lastValueFrom(this.search({ related_user: providerUser }));
                // console.log('previousCredentialingRecords: ', previousCredentialingRecords);

                if (previousCredentialingRecords && previousCredentialingRecords.length) {
                  allPreviousRequiredItems = [];

                  previousCredentialingRecords.forEach(_cr => {
                    // console.log('Checkign credentialing records required items: ', _cr);
                    if (_cr && _cr.required_items && _cr.required_items.length) _cr.required_items.forEach(_ri => allPreviousRequiredItems.push(_ri));
                  });

                  // console.log('allPreviousRequiredItems: ', allPreviousRequiredItems);
                }
              }
            }

            // console.log('Credentialing Setup items for this provider : ', required_items);

            // Used in required_items map and credentialing hire_type field.
            const hireType: string = _placementRecord?.placement_type || _placementRecord?.providers_placements?.provider_type || '';

            // Used to find existing documents related to the provider for uploaded document type items with required expiration date.
            const submittedProviderDocs = provider ? await lastValueFrom(this._documentService.advancedSearch({ providers_documents : provider, status: 'Active' })) : [];
            // console.log('submittedProviderDocs: ', submittedProviderDocs);

            const defaultCompletionDetails = {
              expirationDate: null,
              approvalStatus: '',
              documentLink: null,
              actionValue: null,
              notes: '',
              dateCompleted: null
            };

            const setItems = [];

            for (const _item of required_items) {
              let itemCompletionStatus: boolean = false;
              let skipItemPush: boolean = false;
              let previousItem = null;
              const isUploadedDocType: boolean = _item?.field_type === 'Uploaded Document';

              // if (_item.isOptional) _item.locked = true; // LOCK THIS SO THE PROVIDER DOES NOT SEE IT!!!! Gets unlocked if credentialing specialists moves it to accepted.

              if (allPreviousRequiredItems) {
                // Just filter by label here instead of repeating in finds.
                const relatedItems = allPreviousRequiredItems.filter(_req_item => _req_item?.label === _item?.label);

                if (!isUploadedDocType) {
                  // Other types just find a completed item.
                  previousItem = relatedItems.find(_req_item => _req_item?.completed) ?? null;
                  if (previousItem) itemCompletionStatus = true; // If we don;t want items to be set to completed, this will need reworked.
                }
              }

              // If item is a document type and was not set above, this block handles multiple docs previously uploaded and attached to this item label.
              if (!previousItem && isUploadedDocType && _item?.requires_exp_date) {
                const relatedDocs = submittedProviderDocs.filter(_doc => _doc?.filename === _item?.label || _doc?.name === _item?.label);

                // See if there are none expired documents for this item first. Otherwise, fall back to documents with no expiration_date.
                let docsToAttach = relatedDocs.filter(_doc => _doc?.expiration_date && this.isValidNoneExpiredDate(_doc?.expiration_date)) ?? [];
                if (!docsToAttach.length) docsToAttach = relatedDocs.filter(_doc => !_doc?.expiration_date) ?? [];

                // Add an item for each document found.
                if (docsToAttach.length) {
                  for (const doc of docsToAttach) {
                    const clonedItem = this._sharedUtilsService.deepClone(_item); // Clone so original not effected.
                    if (!clonedItem.completionDetails) clonedItem.completionDetails = defaultCompletionDetails;

                    clonedItem.expiration_date = doc?.expiration_date ?? null;
                    clonedItem.completionDetails.expirationDate = doc?.expiration_date ?? null;
                    clonedItem.completionDetails.approvalStatus = 'Submitted';
                    clonedItem.completionDetails.documentLink = doc?.path ? { link: doc.path } : null;
                    clonedItem.completionDetails.actionValue = doc;
                    clonedItem.completionDetails.dateCompleted = null;
                    clonedItem.completed = false;
                    clonedItem.provider_type = hireType;

                    setItems.push(clonedItem);
                  }

                  skipItemPush = true; // Skip pushing the original _item at the end of the loop since it was pushed for each doc above.
                }
              }

              // Set other fields needed here for any item type.
              _item['completed'] = itemCompletionStatus;
              _item['provider_type'] = hireType;

              if (!skipItemPush) setItems.push(_item);
            }

            // console.log('credentialingSetup to assign out: ', credentialingSetup);
            // console.log('_placementRecord: ', _placementRecord);

            let assignedTo = (_placementRecord['assigned_to']) ? _placementRecord['assigned_to'] : null;

            if (assignedTo && !assignedTo.email) {
              assignedTo = await this._userService.getUser(assignedTo, true);
            }

            // console.log('assignedTo: ', assignedTo);

            const userTeams = _currentUser?.teams?.length ? _currentUser.teams.map((_t) => _t?._id ? _t._id : _t) : [];

            if (!userTeams?.length) {
              const globalTeam = await this._teamService.getGlobalTeam();
              const globalTeamId = (globalTeam['_id'] != undefined) ? globalTeam['_id'] : null;

              if (globalTeamId) userTeams.push(globalTeamId);
            }

            // Fall back to the related user resolved via the provider lookup above when the caller didn't pass one (e.g. manual "Generate Credentialing").
            let _providerUser = relatedUser ?? provider?.related_user ?? null;
            const isMissingNeededDetails: boolean = !_providerUser?._id || !_providerUser?.email; // These fields are needed below.

            // If missing needed fields, get full record.
            if (isMissingNeededDetails) {
              const userId = _providerUser?._id ?? _providerUser;
              if (userId) _providerUser = await this._userService.getUser(userId);
            }

            // Still no usable provider user (the old bug that left credentialing.related_user null and blocked
            // agreements/onboarding). If we have the provider, resolve-or-create one now and persist it back.
            if ((!_providerUser?._id || !_providerUser?.email) && provider?.email) {
              _providerUser = await this._userService.findOrCreateProviderUser(provider);

              if (_providerUser?._id && provider?._id && !provider?.related_user) {
                provider.related_user = _providerUser._id;
                await lastValueFrom(this._genericModuleService.update({
                  record: provider,
                  name: 'providers',
                  schema: providerModule.customSchema
                })).catch(_e => console.error('Failed to persist related_user back to provider:', _e));
              }
            }

            // console.log('_providerUser: ', _providerUser);

            // Removed required_items from credentialing and is now a virtual array.
            const newCredentialingRecord = {
              _id: this._sharedUtilsService.createObjectId(),
              deleted: false,
              name: (_placementRecord?.providers_placements?.name) ? _placementRecord.providers_placements.name : 'View',
              profession: _placementRecord?.profession,
              related_account: _placementRecord?.accounts_placements,
              placements_credentialing: _placementRecord?._id,
              hire_type: hireType,
              credentialing_status: 'In Process - Core',
              providers_credentialing: _placementRecord?.providers_placements,
              related_user: (_providerUser && _providerUser._id) ? _providerUser._id : _providerUser,
              locked: false,
              assigned_to: null, // (assignedTo && assignedTo._id) ? assignedTo._id : assignedTo,
              teams: userTeams
            };

            // assign out the inputs and create credentialing records for each yeahro sign item
            if (setItems && setItems.length) {
              // console.log('Set Items: ', setItems);

              // Build every Yeehro Sign agreement client-side, then save them all in ONE bulk request.
              // Previously this was one browser->nginx->backend round-trip PER agreement (template fetch + save),
              // which burst nginx's api_lim zone, saturated the worker cluster, and could hang mid-batch.
              const approverEmail = (assignedTo && assignedTo.email && assignedTo.email !== undefined) ? assignedTo.email : 'dweber@eduhealthcare.com';
              const itemsNeedingAgreements = setItems.filter(_i => _i?.completionDetails?.actionValue);
              const totalAgreements = itemsNeedingAgreements.length;

              if (totalAgreements) {
                this._subService.setHireStep('agreements', 'active', `Preparing ${totalAgreements} onboarding agreement${totalAgreements === 1 ? '' : 's'}…`);

                // Fetch the dropdown set once for the whole hire.
                const yeehroDropdowns = await lastValueFrom(this._dropdownService.search({ deleted: false }));

                // Pair each item with its template id, then fetch each DISTINCT template only once.
                const itemTemplatePairs = itemsNeedingAgreements.map(_item => ({
                  item: _item,
                  templateId: _item?.completionDetails?.actionValue?._id ? _item.completionDetails.actionValue._id : _item.completionDetails.actionValue
                }));

                const distinctTemplateIds = [...new Set(itemTemplatePairs.map(_p => _p.templateId).filter(Boolean))];
                const templateMap = new Map<string, any>();
                await Promise.all(distinctTemplateIds.map(async (_tid) => {
                  const _tpl = await lastValueFrom(this._interactiveDocumentService.getDocument(_tid)).catch(() => null);
                  if (_tpl) templateMap.set(_tid, _tpl);
                }));

                // Build the instance payloads (CPU-only, no network) and keep a parallel item list to map results back.
                const payloads = [];
                const payloadItems = [];
                for (const _pair of itemTemplatePairs) {
                  const _tpl = templateMap.get(_pair.templateId);
                  if (!_tpl) continue;

                  const _payload = await this.buildAgreementInstance(_tpl, _providerUser, _currentUser, approverEmail, newCredentialingRecord?._id, yeehroDropdowns);
                  if (_payload) {
                    payloads.push(_payload);
                    payloadItems.push(_pair.item);
                  }
                }

                this._subService.setHireStep('agreements', 'active', `Sending ${payloads.length} onboarding agreement${payloads.length === 1 ? '' : 's'}…`);

                // Single round-trip — backend saves them all and returns them in the same order.
                const savedInstances = payloads.length ? await lastValueFrom(this._interactiveDocumentInstanceService.bulkCreate(payloads)).catch(() => null) : [];

                (savedInstances || []).forEach((_inst, _idx) => {
                  const _item = payloadItems[_idx];
                  if (_item && _inst?._id) {
                    _item.relatedAgreement = _inst._id;
                    _item.completionDetails.actionValue = _inst._id;
                  }
                });

                const savedCount = (savedInstances || []).filter(_i => _i?._id).length;
                this._subService.setHireStep('agreements', 'done', `Sent ${savedCount} onboarding agreement${savedCount === 1 ? '' : 's'}.`);
              } else {
                this._subService.setHireStep('agreements', 'done', 'No onboarding agreements needed.');
              }

              const agreementAssignedTo = newCredentialingRecord?.assigned_to;
              newCredentialingRecord.assigned_to = (agreementAssignedTo && agreementAssignedTo._id) ? agreementAssignedTo._id : agreementAssignedTo;

              // If assigned_to is not yet set, then assign current user. Item messages will not send if missing.
              if (newCredentialingRecord?.assigned_to == undefined) {
                const currentLoggedInUser = (_currentUser?._id) ? _currentUser : this._userService.getCurrentlyLoggedInUser();
                newCredentialingRecord.assigned_to = currentLoggedInUser?._id;
              }

              savedCredentialingRecord = await lastValueFrom(this.create(newCredentialingRecord));

              this._subService.setHireStep('items', 'active', 'Building required items…'); // Update process step.

              // Create items for credentialing here and attach them to the virtual array field on the record to be returned incase something needs them.
              const savedRequiredItems = await this._credentialingRequiredItemsService.createRequiredItems(setItems, savedCredentialingRecord?._id);
              newCredentialingRecord['required_items'] = savedRequiredItems ?? [];
              this._subService.setHireStep('items', 'done');

              this._sharedUtilsService.showNotification("Credentialing record created for placement.");
            } else {
              savedCredentialingRecord = await lastValueFrom(this.create(newCredentialingRecord));
              this._sharedUtilsService.showNotification("Credentialing record created for placement.");
            }
          }
        } else {
          this._subService.setHireStep('credentialing', 'error', 'No credentialing setup found for this provider type.');
          this._sharedUtilsService.showNotification("No credentialing setup found for this provider type");
        }

        resolve(savedCredentialingRecord);
      } catch (_err) {
        console.error("Credentialing creation failed:", _err);
        this._subService.setHireStep('credentialing', 'error', 'Failed to create credentialing record.');
        this._sharedUtilsService.showNotification("Failed to create credentialing record.");
        resolve(null);
      }
    });
  }


  isValidNoneExpiredDate(_dateInput: any): boolean {
    const today = new Date();
    const date = new Date(_dateInput);
    return !!_dateInput && !isNaN(date.getTime()) && date > today;
  }


  private getStoredAgreementId(item: any): string | null {
    const fromAction = item?.completionDetails?.actionValue?._id ?? item?.completionDetails?.actionValue ?? null;
    const fromRelated = item?.relatedAgreement?._id ?? item?.relatedAgreement ?? null;
    return fromAction ?? fromRelated ?? null;
  }


  // Resolves a credentialing record's related_user from the linked provider when it's null (originally left null by the buggy Generate Credentialing flow).
  // Returns the user id that was set, or null if nothing to patch / nothing recoverable.
  async patchMissingRelatedUser(credentialingRecord: any): Promise<string | null> {
    if (!credentialingRecord?._id || credentialingRecord?.related_user) return null;

    const providerId = credentialingRecord?.providers_credentialing?._id ?? credentialingRecord?.providers_credentialing ?? null;
    if (!providerId) return null;

    try {
      const providersModule = await this._moduleService.waitForModule('providers');
      const provider = await lastValueFrom(this._genericModuleService.get({
        name: 'providers',
        schema: providersModule?.customSchema,
        searchTerms: { _id: providerId }
      })).catch(() => null);

      const userId = provider?.related_user?._id ?? provider?.related_user ?? null;
      if (!userId) return null;

      await lastValueFrom(this.updateFieldsById(credentialingRecord._id, { related_user: userId }));
      credentialingRecord.related_user = userId;
      return userId;
    } catch (err) {
      console.error('patchMissingRelatedUser error:', err);
      return null;
    }
  }


  // Returns Yeehro Sign items whose stored agreement id no longer resolves to an instance.
  // Used by the admin "X items need repair" affordance on the credentialing profile.
  async findItemsNeedingAgreementRepair(items: any[]): Promise<any[]> {
    if (!items?.length) return [];

    const signItems = items.filter(_i => _i?.field_type === 'Yeehro Sign Document');
    if (!signItems.length) return [];

    // Pair each sign item with its stored agreement id, then check existence in ONE request instead of
    // firing a heavy getInstance GET per item (which flooded the worker cluster on profile load).
    const itemIdPairs = signItems
      .map(_item => ({ item: _item, id: this.getStoredAgreementId(_item) }))
      .filter(_p => _p.id); // No id stored → nothing to repair off of, leave alone.

    if (!itemIdPairs.length) return [];

    const existingIds = await lastValueFrom(this._interactiveDocumentInstanceService.existingInstanceIds(itemIdPairs.map(_p => String(_p.id)))).catch(() => null);

    // If the check itself failed, don't flag everything as broken.
    if (existingIds === null) return [];

    const existingSet = new Set(existingIds);
    return itemIdPairs.filter(_p => !existingSet.has(String(_p.id))).map(_p => _p.item);
  }


  // Recreate the missing agreement instance for a single broken required item, then persist the new id back to the item.
  // Returns the saved instance id, or null if the item couldn't be recovered (e.g. id isn't a template, or no provider user available).
  async repairMissingAgreement(item: any, credentialingRecord: any, currentUser: any): Promise<string | null> {
    try {
      const storedId = this.getStoredAgreementId(item);
      if (!storedId) return null;

      // If it now resolves, just persist that id consistently.
      const existingInstance = await lastValueFrom(this._interactiveDocumentInstanceService.getInstance(storedId)).catch(() => null);
      if (existingInstance?._id) return existingInstance._id;

      // Confirm the stale id actually points at a template we can clone from.
      const template = await lastValueFrom(this._interactiveDocumentService.getDocument(storedId)).catch(() => null);
      if (!template?._id) return null;

      // Resolve the provider's related user. The bug that created these items left related_user null, so we fall back to the provider record.
      let providerUser: any = credentialingRecord?.related_user ?? null;
      if (!providerUser?._id) {
        const providerId = credentialingRecord?.providers_credentialing?._id ?? credentialingRecord?.providers_credentialing ?? null;
        if (providerId) {
          const providersModule = await this._moduleService.waitForModule('providers');
          const provider = await lastValueFrom(this._genericModuleService.get({
            name: 'providers',
            schema: providersModule?.customSchema,
            searchTerms: { _id: providerId }
          })).catch(() => null);
          providerUser = provider?.related_user ?? providerUser;
        }
      }
      const providerUserId = providerUser?._id ?? providerUser ?? null;
      if (providerUserId && (!providerUser?._id || !providerUser?.email)) {
        providerUser = await this._userService.getUser(providerUserId);
      }
      if (!providerUser?._id || !providerUser?.email) return null;

      // Approver email mirrors what checkAndCreateCredentialing does — assigned_to email, falling back to the acting user.
      let approverEmail: string | null = credentialingRecord?.assigned_to?.email ?? null;
      if (!approverEmail) {
        const assignedToId = credentialingRecord?.assigned_to?._id ?? credentialingRecord?.assigned_to ?? null;
        if (assignedToId) {
          const assignedUser = await this._userService.getUser(assignedToId, true).catch(() => null);
          approverEmail = assignedUser?.email ?? null;
        }
      }
      if (!approverEmail) approverEmail = currentUser?.email ?? null;
      if (!approverEmail) return null;

      const savedAgreement: any = await this.createAgreementAndSend(storedId, providerUser, currentUser, approverEmail, credentialingRecord?._id);
      if (!savedAgreement?._id) return null;

      item.relatedAgreement = savedAgreement._id;
      if (!item.completionDetails) item.completionDetails = {};
      item.completionDetails.actionValue = savedAgreement._id;
      await lastValueFrom(this._credentialingRequiredItemsService.update(item));

      // Patch related_user on the credentialing record if it was the original null left behind by the bug.
      if (!credentialingRecord?.related_user && credentialingRecord?._id) {
        credentialingRecord.related_user = providerUser._id;
        await lastValueFrom(this.updateFieldsById(credentialingRecord._id, { related_user: providerUser._id })).catch(() => null);
      }

      return savedAgreement._id;
    } catch (err) {
      console.error('repairMissingAgreement error:', err);
      return null;
    }
  }


  getRelatedAccountId(_placementRecord: any): Promise<string | null> {
    return new Promise(async (resolve) => {
      try {
        const placementSub = _placementRecord['subs_placements'];
        let relatedAccountId: string | null = (_placementRecord['accounts_placements'] && _placementRecord['accounts_placements']._id) ? _placementRecord['accounts_placements']._id : _placementRecord['accounts_placements'];

        if (!relatedAccountId) {
          if (placementSub['accounts_subs']) {
            relatedAccountId = (placementSub['accounts_subs'] && placementSub['accounts_subs']._id) ? placementSub['accounts_subs']._id : placementSub['accounts_subs'];
          } else if (placementSub) {
            const subId = placementSub['_id'] ?? placementSub;
            const fullSub = await lastValueFrom(this._subService.getSub(subId));
            if (fullSub['accounts_subs']) relatedAccountId = fullSub['accounts_subs']?._id ?? fullSub['accounts_subs'];
          }
        }

        resolve(relatedAccountId);
      } catch (_err) {
        console.log('Error getting related account id for placement: ', _err);
        resolve(null);
      }
    });
  }


  // Build (client-side only, no network) the instance payload for a Yeehro Sign template: applies the
  // participant/approver assignment + dropdown fixups and stamps the agreement fields. Pulled out of
  // createAgreementAndSend so the hire flow can build many payloads and save them in one bulk request.
  async buildAgreementInstance(_yeehroSignTemplate, _providerUser, _currentUserLoggedIn, _approverEmail, _credentialingRecordId, _preloadedDropdowns = null): Promise<InteractiveDocumentInstance> {
    if (!_yeehroSignTemplate || !_providerUser) return null;

    if (_currentUserLoggedIn == undefined) _currentUserLoggedIn = this._userService.getCurrentlyLoggedInUser(); // Fallback if null.

    // Dropdowns with related_dropdown use cases need recent db options. Reuse the preloaded set when provided.
    const yeehroDropdowns = _preloadedDropdowns ?? await lastValueFrom(this._dropdownService.search({ deleted: false }));

    (_yeehroSignTemplate?.doc_data?.pages || []).forEach((_page, _pageIndex) => {
      if (!_page?.inputs) _page.inputs = [];

      for (const [_inputIndex, _input] of _page.inputs.entries()) {
        if (_input?.assigned_to?.toLowerCase()?.includes('participant') || _input?.field?.assigned_to?.toLowerCase()?.includes('participant')) {
          _yeehroSignTemplate.doc_data.pages[_pageIndex].inputs[_inputIndex].assigned_to = _providerUser?.email;
          _yeehroSignTemplate.doc_data.pages[_pageIndex].inputs[_inputIndex].field.assigned_to = _providerUser?.email;
        } else if (['Approver', 'Sender (Prefill)'].includes(_input?.assigned_to) || ['Approver', 'Sender (Prefill)'].includes(_input?.field?.assigned_to)) {
          _yeehroSignTemplate.doc_data.pages[_pageIndex].inputs[_inputIndex].assigned_to = _approverEmail;
          _yeehroSignTemplate.doc_data.pages[_pageIndex].inputs[_inputIndex].field.assigned_to = _approverEmail;
        }

        this._interactiveDocumentService.makeInputAdjustments(_input, yeehroDropdowns);
      }
    });

    let assigned_to = (_yeehroSignTemplate.assigned_to && _yeehroSignTemplate.assigned_to._id) ? _yeehroSignTemplate.assigned_to._id : _yeehroSignTemplate.assigned_to;
    if (this._sharedUtilsService.isArray(assigned_to)) assigned_to = assigned_to[0];

    const documentAgreement = <InteractiveDocumentInstance>{ ..._yeehroSignTemplate };
    documentAgreement.interactive_document = _yeehroSignTemplate._id;
    documentAgreement.status = 'Approved';
    documentAgreement.createdAt = new Date();
    documentAgreement.updatedAt = new Date();
    documentAgreement.relatedCredentialingRecordId = _credentialingRecordId;
    documentAgreement.assigned_to = assigned_to || _providerUser?._id || _providerUser;
    documentAgreement.created_by = (_yeehroSignTemplate?.created_by != undefined) ? _yeehroSignTemplate.created_by : _currentUserLoggedIn;
    documentAgreement.recipients = [
      { recipientId: 1, recipientType: "Sender (Prefill)", fromType: "to", email: 'ignored-role', email2: _approverEmail },
      { recipientId: 2, recipientType: "Participant 1", fromType: "to", email: _providerUser?.email }
    ];

    // fields are copied over from template. Id needs to be removed.
    delete documentAgreement._id;
    return documentAgreement;
  }


  // _preloadedDropdowns lets a caller pass the dropdown set in so it isn't re-fetched per agreement.
  // When omitted (single-agreement callers) it falls back to fetching, so existing callers are unaffected.
  async createAgreementAndSend(_idTemplateId, _providerUser, _currentUserLoggedIn, _approverEmail, _credentialingRecordId, _preloadedDropdowns = null): Promise<InteractiveDocumentInstance> {
    const _yeehroSignTemplate = await lastValueFrom(this._interactiveDocumentService.getDocument(_idTemplateId));

    const documentAgreement = await this.buildAgreementInstance(_yeehroSignTemplate, _providerUser, _currentUserLoggedIn, _approverEmail, _credentialingRecordId, _preloadedDropdowns);
    if (!documentAgreement) return null;

    return await lastValueFrom(this._interactiveDocumentInstanceService.create(documentAgreement));
  }


  createTaskAndMessageForCredentialing(_credentialingRecord: any) {
    return new Promise(async (resolve) => {
      let savedTask = null; // So we can return this incase we need to use it for something.

      if (_credentialingRecord?._id) {
        // A new task for credentialing@eduhealthcare.com needs to be created.
        const relatedRecord = { moduleName: 'providers', recordId: _credentialingRecord?.providers_credentialing?._id ?? _credentialingRecord?.providers_credentialing }
        const startDate = new Date();
        const dueDate = new Date(startDate); // Start at start date.
        dueDate.setDate(dueDate.getDate() + 30); // Adjust amount of days as needed.

        try {
          const newTask = new Task();
          newTask.assigned_to = '62796c4661d60b20360b8d53'; // Lynne Prod: "62796c4661d60b20360b8d53" Justin Dev: "5e57d63e7e4f363784fcdf59",
          newTask.name = 'A New Credentialing was Created for a Provider';
          newTask.subject = 'A New Credentialing was Created for a Provider';
          newTask.status = 'Not Started';
          newTask.task_type = 'Credentialing';
          newTask.start_date = startDate;
          newTask.due_date = dueDate;
          newTask.description = 'A New Credentialing was Created for a Provider';
          newTask.parent_type = 'providers';
          newTask.deleted = false;
          newTask['providers_tasks'] = relatedRecord?.recordId ?? null;
          newTask.related_record = relatedRecord;

          savedTask = await lastValueFrom(this._taskService.create(newTask));

          if (savedTask?._id) {
            // now send a notification email to person assigned to the task
            const newMessage = new Message();
            newMessage.name = 'New Task Assigned';
            newMessage.subject = 'New Task Assigned';
            newMessage.to = 'credentialing@eduhealthcare.com';
            newMessage.sentFrom = 'yeehro@eduhealthcare.com';
            newMessage.direction = 'outgoing';
            newMessage.status = 'Sent';
            newMessage.message = `A new credentialing record was created for a provider. <a href="https://www.yeehro.com/app/tasks/${savedTask._id}">View Task</a>`;
            newMessage.messageType = 'email';
            newMessage.deleted = false;

            // console.log('Would send alert email: ', newMessage);

            await lastValueFrom(this._messageService.sendAlertEmail({ messageRecord: newMessage }));
          }
        } catch (_err) {
          console.log('There was an issue creating task and message for credential.');
        }
      }

      resolve(savedTask);
    });
  }
}